// ApiAccessForm (/api/access) — request-access form.
//
// Captures interest signups and POSTs to /api/v1/access/request. The actual
// data submission, rate-limit, DB insert, and email notification are handled
// by the Pages Function at functions/api/v1/access/request.ts.
//
// Visual language matches /api (APIAccess.tsx) and the rest of the AHP
// design system: patriotic palette, Instrument Serif headlines, Instrument
// Sans body, soft eyebrow chips, single-column form on a card.

import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CheckCircle2, Mail, Send } from "lucide-react";

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";

function useThemeTokens() {
  const { isDark } = useTheme();
  return useMemo(() => {
    if (isDark) {
      return {
        bg: "#0a1628", bgAlt: "#050e1a",
        text: "#FAF9F6", textMuted: "rgba(250,249,246,0.6)",
        gold: "#C5A55A", red: "#B22234", blue: "#5B9BD5", navy: "#002868",
        cardBg: "#0d1e35", cardBorder: "rgba(250,249,246,0.10)",
        divider: "rgba(250,249,246,0.10)",
        chipBg: "rgba(197,165,90,0.10)",
        success: "#6bc9a0",
      };
    }
    return {
      bg: "#FAF9F6", bgAlt: "#ffffff",
      text: "#1B1B1B", textMuted: "rgba(27,27,27,0.62)",
      gold: "#C5A55A", red: "#B22234", blue: "#002868", navy: "#002868",
      cardBg: "#ffffff", cardBorder: "rgba(27,27,27,0.08)",
      divider: "rgba(27,27,27,0.08)",
      chipBg: "rgba(197,165,90,0.10)",
      success: "#2b5e49",
    };
  }, [isDark]);
}

// Match the ALLOWED_VOLUMES set on the Pages Function so the server doesn't
// reject a value the dropdown offered. Order is small → large + "Not sure".
const VOLUME_OPTIONS = [
  "<1K/day",
  "1K-10K/day",
  "10K-100K/day",
  "100K+/day",
  "Not sure",
] as const;

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

export default function ApiAccessForm() {
  const T = useThemeTokens();
  const brand = useBrand();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [useCase, setUseCase] = useState("");
  const [expectedVolume, setExpectedVolume] = useState<string>("");
  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  const canSubmit = email.trim().length > 0 && state.kind !== "submitting";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setState({ kind: "submitting" });

    try {
      const resp = await fetch("/api/v1/access/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          name: name.trim() || undefined,
          company: company.trim() || undefined,
          use_case: useCase.trim() || undefined,
          expected_volume: expectedVolume || undefined,
        }),
      });

      if (resp.ok) {
        setState({ kind: "success" });
        return;
      }

      // Map known error codes to a user-readable message — anything else
      // falls back to a generic try-again line so we don't expose stack
      // traces or DB errors to the visitor.
      let message = "Something went wrong. Please try again.";
      try {
        const body = (await resp.json()) as { error?: string };
        if (resp.status === 429) {
          message = "Too many submissions from your network today. Please try again tomorrow.";
        } else if (body.error === "invalid_email") {
          message = "That email doesn't look quite right. Please check and try again.";
        } else if (body.error === "invalid_expected_volume") {
          message = "Please pick one of the listed volume options.";
        } else if (resp.status >= 500) {
          message = "Our form server is having a moment. Try again in a few minutes.";
        }
      } catch {
        // body wasn't JSON, keep the generic message
      }
      setState({ kind: "error", message });
    } catch {
      setState({
        kind: "error",
        message:
          "Could not reach the server. Check your connection and try again.",
      });
    }
  };

  // ── Success state — replace the form with a thank-you panel ────────────
  if (state.kind === "success") {
    return (
      <div style={{ background: T.bg, color: T.text, minHeight: "100vh", fontFamily: sans }}>
        <Helmet>
          <title>API access requested — {brand?.name ?? "American Hospital Prices"}</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <div className="mx-auto px-6 flex items-center justify-center" style={{ maxWidth: 640, minHeight: "70vh" }}>
          <div
            className="w-full rounded-lg p-10 text-center"
            style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}
          >
            <div
              className="mx-auto rounded-full flex items-center justify-center mb-6"
              style={{ width: 60, height: 60, background: `${T.success}22` }}
            >
              <CheckCircle2 className="w-8 h-8" style={{ color: T.success }} />
            </div>
            <h1 style={{ fontFamily: serif, fontSize: "2rem", color: T.text, marginBottom: 12 }}>
              We've received your request
            </h1>
            <p style={{ color: T.textMuted, lineHeight: 1.6, marginBottom: 24 }}>
              Thanks for reaching out. Our team will follow up at the email
              you provided. In the meantime, the live API surface is in{" "}
              <Link to="/api/docs" style={{ color: T.gold, textDecoration: "underline" }}>
                /api/docs
              </Link>{" "}
              and the methodology behind the data is on{" "}
              <Link to="/data-methodology" style={{ color: T.gold, textDecoration: "underline" }}>
                /data-methodology
              </Link>.
            </p>
            <Link
              to="/api"
              className="inline-flex items-center gap-2"
              style={{
                background: T.gold, color: "#1B1B1B",
                padding: "10px 20px", borderRadius: 4,
                fontWeight: 700, fontSize: "0.9rem", textDecoration: "none",
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to API overview
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: T.bg, color: T.text, minHeight: "100vh", fontFamily: sans }}>
      <Helmet>
        <title>Request API access — {brand?.name ?? "American Hospital Prices"}</title>
        <meta
          name="description"
          content="Request programmatic access to American Hospital Prices hospital pricing data."
        />
      </Helmet>

      <div className="mx-auto px-6" style={{ maxWidth: 720, paddingTop: 64, paddingBottom: 96 }}>
        <Link
          to="/api"
          className="inline-flex items-center gap-1 mb-6 hover:opacity-80 transition-opacity"
          style={{ color: T.textMuted, fontSize: "0.85rem", textDecoration: "none" }}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          API overview
        </Link>

        <div className="mb-8">
          <span
            className="inline-block"
            style={{
              fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase",
              color: T.gold, fontWeight: 700,
              background: T.chipBg, padding: "4px 10px", borderRadius: 4,
            }}
          >
            Request access
          </span>
          <h1
            style={{
              fontFamily: serif, fontSize: "2.4rem", lineHeight: 1.1,
              color: T.text, marginTop: 16, letterSpacing: "-0.01em",
            }}
          >
            Programmatic access to AHP pricing data.
          </h1>
          <p style={{ color: T.textMuted, marginTop: 14, lineHeight: 1.55, maxWidth: 560 }}>
            Tell us who you are and what you're building. We'll follow up to
            confirm your use case and get you set up. Anonymous browsing of
            the data on americanhospitalprices.com is always free; this form is
            for higher-volume programmatic access.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-lg p-6 md:p-8 space-y-5"
          style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}
        >
          <div>
            <Label htmlFor="email" style={{ color: T.text }}>
              Email <span style={{ color: T.red }}>*</span>
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" style={{ color: T.text }}>Your name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="company" style={{ color: T.text }}>Organization</Label>
              <Input
                id="company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Your company, university, newsroom, etc."
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="expected_volume" style={{ color: T.text }}>
              Expected request volume
            </Label>
            <Select value={expectedVolume} onValueChange={setExpectedVolume}>
              <SelectTrigger id="expected_volume" className="mt-1">
                <SelectValue placeholder="Pick a range" />
              </SelectTrigger>
              <SelectContent>
                {VOLUME_OPTIONS.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="use_case" style={{ color: T.text }}>
              What are you building?
            </Label>
            <Textarea
              id="use_case"
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              placeholder="A patient-facing price comparison tool, a research dataset, a journalism investigation, an internal dashboard…"
              rows={4}
              className="mt-1"
            />
            <p className="mt-1.5" style={{ fontSize: "0.75rem", color: T.textMuted }}>
              Optional — helps us route your request and suggest the right data slice.
            </p>
          </div>

          {state.kind === "error" && (
            <div
              className="rounded p-3 text-sm"
              style={{
                background: `${T.red}10`,
                border: `1px solid ${T.red}30`,
                color: T.red,
              }}
            >
              {state.message}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 pt-2">
            <p style={{ fontSize: "0.75rem", color: T.textMuted, maxWidth: 380 }}>
              We'll only use your contact info to follow up about API access.
              No marketing list, no resale.
            </p>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2"
              style={{
                background: T.gold, color: "#1B1B1B",
                fontWeight: 700, fontSize: "0.9rem",
                opacity: canSubmit ? 1 : 0.6,
              }}
            >
              {state.kind === "submitting" ? "Sending..." : "Request access"}
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>

        <div
          className="mt-8 rounded-lg p-5 flex items-start gap-3"
          style={{ background: T.bgAlt, border: `1px solid ${T.cardBorder}` }}
        >
          <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: T.gold }} />
          <div style={{ fontSize: "0.85rem", color: T.textMuted, lineHeight: 1.55 }}>
            Prefer to write directly? Email{" "}
            <a
              href="mailto:api-request@americanhospitalprices.com"
              style={{ color: T.gold, textDecoration: "underline" }}
            >
              api-request@americanhospitalprices.com
            </a>
            {" "}with your use case and we'll pick it up from there.
          </div>
        </div>
      </div>
    </div>
  );
}
